import { Button, Spin, Input } from "antd";
import axios from "axios";
import { useState, useEffect } from "react";
import Sidebar from "../../GlobalFiles/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { API_BASE_URL } from "../../../../../api";
import { GetPatients } from "../../../../../Redux/Datas/action";
import { useNavigate, useLocation } from "react-router-dom";
import { FileText, Group } from "lucide-react";
import DoctorSelect from "../../../../../Components/Dropdowns/DoctorSelect";
import { toast } from "react-toastify";
import { BsPerson } from "react-icons/bs";
import TestUnitSelect from "../../../../../Components/Dropdowns/TestUnitSelect";
import TestValueSelect from "../../../../../Components/Dropdowns/TestValueSelect";
import isTextTypeReport from "../../../../../helper/isTextTypeReport";
import PatientSection from "./AddReport/PatientSection";
import { GroupedTestModal, PatientInfoModal, SelectTestOrderModal } from "./AddReport/ReportModals";
import { getResultStatus, REPORT_STATUS } from "./AddReport/reportStatus";


const AddReport = () => {
  const [loading, setLoading] = useState(false);

  const [patients, setPatients] = useState([]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedTestOrder, setSelectedTestOrder] = useState(null);

  const [selectableTestOrders, setSelectableTestOrders] = useState([]);
  const [currentModal, setCurrentModal] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormdata] = useState({
    patient: selectedPatient?._id || "",
    testOrderId: selectedTestOrder?._id || "",
    doctor: selectedTestOrder?.doctor?._id || "",
    agent: selectedTestOrder?.agent || "",
    preparedBy: selectedTestOrder?.preparedBy?._id || selectedTestOrder?.preparedBy || "",
    comment: "",
    results: []
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!location.state?.testOrder;

  const { data: { user }, } = useSelector((state) => state.auth);
  const isManager = user?.userType === "manager";

  useEffect(() => {
    if (!isManager) {
      navigate('/');
      return;
    }
    const fetchAllData = async () => {
      setLoading(true);
      const fetchedPatients = await fetchPatients();

      // If we navigated here with a pre-selected testOrder or report in state
      if (location.state?.testOrder || location.state?.report) {
        const inputReport = location.state?.report || location.state?.testOrder?.report;
        const orderId = (location.state.testOrder?._id || location.state.testOrder || inputReport?.testOrder?._id || inputReport?.testOrder)?.toString();
        const patientId = (location.state.testOrder?.patient?._id || location.state.testOrder?.patient || inputReport?.patient?._id || inputReport?.patient)?.toString();

        // Find the full patient from fetched data
        const p = fetchedPatients?.find(p => p._id?.toString() === patientId);
        if (p) {
          setSelectedPatient(p);
          const tOrder = p.testOrders?.find(t => t._id?.toString() === orderId);
          if (tOrder) {
            const mergedReport = (inputReport?.results && inputReport.results.length > 0)
              ? inputReport
              : (tOrder.report?.results && tOrder.report.results.length > 0)
                ? tOrder.report
                : (inputReport || tOrder.report);
            setSelectedTestOrder({ ...tOrder, report: mergedReport });
          } else if (location.state.testOrder) {
            setSelectedTestOrder({
              ...(typeof location.state.testOrder === 'object' ? location.state.testOrder : {}),
              report: inputReport || location.state.testOrder?.report
            });
          }
        } else if (location.state.testOrder) {
          if (location.state.testOrder.patient && typeof location.state.testOrder.patient === 'object') {
            setSelectedPatient(location.state.testOrder.patient);
          }
          setSelectedTestOrder({
            ...(typeof location.state.testOrder === 'object' ? location.state.testOrder : {}),
            report: inputReport || location.state.testOrder?.report
          });
        }
      }
      setLoading(false);
    };

    if (isManager) {
      dispatch(GetPatients());
      fetchAllData();
    }
  }, [dispatch]);

  useEffect(() => {
    if (!selectedTestOrder || !selectedPatient) return;

    const activeReport = selectedTestOrder?.report || location.state?.report;
    const reportResults = activeReport?.results || [];

    const baseTests = selectedTestOrder?.tests || [];
    const combinedTests = [...baseTests];

    if (reportResults.length > 0) {
      reportResults.forEach(r => {
        const rTestObj = (r?.test && typeof r.test === 'object') ? r.test : {};
        const rTestId = (rTestObj._id || r?.test || r?._id)?.toString();
        const rTestName = (r?.testName || rTestObj.testName || "")?.trim().toLowerCase();

        const exists = combinedTests.some(t => {
          const tTestObj = (t?.test && typeof t.test === 'object') ? t.test : {};
          const tTestId = (tTestObj._id || t?.test || t?._id)?.toString();
          const tTestName = (tTestObj.testName || t?.testName || "")?.trim().toLowerCase();
          return (rTestId && tTestId && rTestId === tTestId) || (rTestName && tTestName && rTestName === tTestName);
        });

        if (!exists) {
          combinedTests.push({
            test: rTestObj._id ? rTestObj : (r.test || r._id),
            testName: r.testName || rTestObj.testName || rTestObj.title,
            testUnit: r.testUnit || rTestObj.testUnit,
            normalRange: r.normalValue || rTestObj.normalRange
          });
        }
      });
    }

    setFormdata({
      patient: selectedPatient._id,
      testOrderId: selectedTestOrder._id,
      doctor: selectedTestOrder?.doctor?._id || selectedPatient?.doctor || null,
      preparedBy: activeReport?.preparedBy?._id || activeReport?.preparedBy || null,
      comment: activeReport?.comment || null,
      results: combinedTests.map(t => {
        const testObj = (t?.test && typeof t.test === 'object') ? t.test : {};
        const isTextType = isTextTypeReport(testObj?.testGroup?.superGroup);

        const tTestId = (testObj._id || t?.test || t?._id)?.toString();
        const tTestName = (testObj.testName || t?.testName || "")?.trim().toLowerCase();

        const currRes = reportResults.find(r => {
          const rTestObj = (r?.test && typeof r.test === 'object') ? r.test : {};
          const rTestId = (rTestObj._id || r?.test || r?._id)?.toString();
          const rTestName = (r?.testName || rTestObj.testName || "")?.trim().toLowerCase();

          const idMatch = Boolean(rTestId && tTestId && rTestId === tTestId);
          const nameMatch = Boolean(rTestName && tTestName && rTestName === tTestName);
          return idMatch || nameMatch;
        });

        const valueFromTemplate = isTextType ? (testObj.normalRange || "") : "";
        const seededValue = (currRes?.value !== undefined && currRes?.value !== null) ? currRes.value : valueFromTemplate;

        const seededUnit = isTextType
          ? (currRes?.testUnit || "")
          : (currRes?.testUnit || testObj.testUnit || "");
        const seededNormalValue = isTextType
          ? (currRes?.normalValue || "")
          : (currRes?.normalValue || testObj.normalRange || "");

        const resultWithSeededValues = {
          ...currRes,
          value: seededValue,
          testUnit: seededUnit,
          normalValue: seededNormalValue,
        };

        return {
          test: testObj._id || t?.test || t?._id || null,
          testName: currRes?.testName || testObj.testName || testObj.title || t?.testName || "",
          value: seededValue,
          testUnit: seededUnit,
          normalValue: seededNormalValue,
          status: getResultStatus(resultWithSeededValues, isTextType)
        };
      })
    });
  }, [selectedTestOrder, selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/patients?depth=2`);
      const patientsData = response.data?.patients || response.data || [];
      setPatients(patientsData);
      return patientsData;
    } catch (error) {
      console.error("Error fetching patients:", error);
      return [];
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    if (patient === null) {
      setSelectedTestOrder(null);
      return;
    }
    const testOrdersForPatient = patient.testOrders;
    if (!testOrdersForPatient || testOrdersForPatient.length === 0) {
      setSelectedTestOrder(null);
      toast.error("This patient has no test order.", { toastId: "noTestOrderError" });
      return;
    }
    if (testOrdersForPatient.length > 1) {
      setSelectableTestOrders(testOrdersForPatient);
      setCurrentModal("select-test-order");
    } else {
      setSelectedTestOrder(testOrdersForPatient[0]);
      setCurrentModal("");
      toast.success("This patient has single test order", { toastId: "patientHasSingleTestOrder" });
    }
  };

  const patientLookup = (() => {
    const lookup = {
      byId: new Map(),
      byMobile: new Map(),
      byName: new Map(),
    };

    if (!Array.isArray(patients)) return lookup;

    patients.forEach((p) => {
      if (!p) return;
      if (p._id) lookup.byId.set(p._id.toString(), p);
      if (p.patientID) lookup.byId.set(p.patientID.toString(), p);
      if (p.mobile) lookup.byMobile.set(p.mobile.toString(), p);
      if (p.patientName) lookup.byName.set(p.patientName.toLowerCase(), p);
    });

    return lookup;
  })();

  const resolvePatientFromOrder = (order) => {
    if (!order) return null;
    if (order.patient && typeof order.patient === "object") return order.patient;
    if (!patients) return null;

    const patientId = order.patientId || order.patientID || order.patient?.patientID || order.patient?._id || order.patient;
    if (patientId) {
      const byId = patientLookup.byId.get(patientId.toString());
      if (byId) return byId;
    }

    const patientMobile = order.patient?.mobile || order.mobile;
    if (patientMobile) {
      const byMobile = patientLookup.byMobile.get(patientMobile.toString());
      if (byMobile) return byMobile;
    }

    const patientName = order.patient?.patientName || order.patientName;
    if (patientName) {
      const byName = patientLookup.byName.get(patientName.toLowerCase());
      if (byName) return byName;
    }

    return null;
  };

  const handleTestOrderSelect = (order) => {
    if (!order) return;
    const resolvedPatient = resolvePatientFromOrder(order);
    if (resolvedPatient) {
      setSelectedPatient(resolvedPatient);
    }
    setSelectedTestOrder(order);
    setCurrentModal("");
  };


  const handleReportSave = async () => {
    // Guard: no test order selected
    if (!selectedTestOrder) {
      toast.warn("Please select a test order before saving.", { toastId: "no-test-order" });
      return;
    }

    // Guard: prepared by doctor is required
    if (!formData.preparedBy) {
      toast.warn("Prepared by doctor is missing. Please select a doctor.", { toastId: "no-prepared-by" });
      return;
    }

    setIsSaving(true);

    try {
      const isUpdating = Boolean(selectedTestOrder?.report?._id);
      const resolvedPatientId =
        formData.patient ||
        selectedPatient?._id ||
        selectedTestOrder?.patient?._id ||
        selectedTestOrder?.patientId ||
        selectedTestOrder?.patientID ||
        selectedTestOrder?.patient ||
        null;
      const resolvedTestOrderId = formData.testOrderId || selectedTestOrder?._id || null;
      const reportStatus = formData.results?.length > 0 && formData.results.every((result) => result.status === REPORT_STATUS.COMPLETED)
        ? REPORT_STATUS.COMPLETED
        : REPORT_STATUS.PENDING;
      const payload = {
        ...formData,
        status: reportStatus,
        patient: resolvedPatientId,
        testOrder: resolvedTestOrderId,
        testOrderId: resolvedTestOrderId,
      };
      const url = isUpdating ? `${API_BASE_URL}/reports/${selectedTestOrder.report._id}` : `${API_BASE_URL}/reports/create`;
      const method = isUpdating ? axios.patch : axios.post;
      const res = await method(url, payload);

      if (res.status === 200 && res.data?.report) {
        const updatedReport = res.data.report;
        const patientId = selectedPatient?._id;
        const testOrderId = selectedTestOrder?._id;

        setPatients(prev =>
          prev.map(p =>
            p._id !== patientId
              ? p
              : {
                ...p,
                testOrders: (p.testOrders ?? []).map(to =>
                  to._id !== testOrderId ? to : { ...to, report: updatedReport }
                ),
              }
          )
        );

        setSelectedTestOrder(prev =>
          prev ? { ...prev, report: updatedReport, comment: formData.comment } : prev
        );

        setSelectedPatient(prev =>
          prev
            ? {
              ...prev,
              testOrders: (prev.testOrders ?? []).map(to =>
                to._id !== testOrderId ? to : { ...to, report: updatedReport }
              ),
            }
            : prev
        );

        toast.success(
          isUpdating ? "Report updated successfully." : "Report saved successfully.",
          { toastId: isUpdating ? "update-success" : "save-success", autoClose: 2000 }
        );
      } else {
        toast.error(
          isUpdating ? "Error updating report. Please try again." : "Error saving report. Please try again.",
          { toastId: isUpdating ? "update-error" : "save-error" }
        );
      }
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error("Error saving report. Please try again.", { toastId: "save-error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCommentChange = (newComment) => {
    setFormdata((prevData) => ({
      ...prevData,
      comment: newComment,
    }));
  };

  const setFormTestValue = (idx, fieldName, newValue) => {
    setFormdata((prevData) => ({
      ...prevData,
      results: prevData.results.map((r, i) => {
        if (i !== idx) return r;

        const updated = { ...r, [fieldName]: newValue };

        // Text-type tests only need a value to be considered complete —
        // unit and normalValue are intentionally blank for them.
        const refTest = selectedTestOrder?.tests?.[idx];
        const isTextType = isTextTypeReport(refTest?.test?.testGroup?.superGroup);

        return {
          ...updated,
          status: getResultStatus(updated, isTextType),
        };
      }),
    }));
  };

  const getGroupedTests = () => {
    if (!selectedTestOrder || !Array.isArray(selectedTestOrder.tests)) return null;

    const grouped = selectedTestOrder.tests.reduce((acc, curr) => {
      const group = curr?.test?.category || "Other";
      if (!acc[group]) {
        acc[group] = [];
      }

      const curRes = formData?.results?.find(r => (r?.test === curr?.test?._id || r?.test === curr));
      acc[group].push({
        ...curr,
        status: curRes?.status || REPORT_STATUS.PENDING,
      });
      return acc;

    }, {});
    return grouped;
  };

  return (
    <div>
      <div className="flex min-h-screen bg-gray-50 overflow-x-hidden">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 min-w-0 p-3 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'
          }`}>
          <div className="flex-1 min-w-0 p-3 ml-40">
            {/* Header Section */}
            <div className="mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-5 rounded-3xl"></div>
              <div className="relative z-10 p-5">
                <div className="flex items-center space-x-4 mb-2">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <FileText className="text-white text-xl" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                      {isEditMode ? "Edit Report" : "Create Report"}
                    </h1>
                    <p className="text-slate-600 text-lg font-medium">Create or modify medical reports</p>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 p-12 text-center">
                  <Spin size="large" className="mb-4" />
                  <p className="text-slate-600 text-lg font-medium">Loading report data...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Test Result Section */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <FileText className="text-white text-lg" />
                      </div>
                      <h2 className="text-2xl font-bold text-white">Test Results</h2>
                      {selectedTestOrder && <div className="text-white/80">{formData?.results?.filter(t => t.status === "completed").length || 0}/{formData?.results?.length || selectedTestOrder?.tests?.length || 0} completed</div>}
                    </div>
                    <div className="flex items-center space-x-3 gap-2">
                      {
                        selectedPatient && <Button
                          onClick={() => setCurrentModal("patient-info")}
                          className="bg-white/20 hover:bg-white/30 text-white font-semibold px-2 rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <BsPerson className="inline-block m-2 my-auto" /> Patient Info
                        </Button>}
                      {selectedTestOrder && <Button
                        onClick={() => setCurrentModal("grouped-test")}
                        className="bg-white/20 hover:bg-white/30 text-white font-semibold px-2 rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <Group className="inline-block m-2 my-auto" /> Grouped Test
                      </Button>}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="overflow-x-auto h-[300px] overflow-y-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-gray-100">
                            <th className="text-left py-4 px-4 font-semibold text-slate-700   w-[5%]">SL</th>
                            <th className="text-left py-4 px-4 font-semibold text-slate-700   w-[20%]">Test</th>
                            <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[20%]">Result</th>
                            <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[15%]">Test Unit</th>
                            <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[20%]">Normal</th>
                            <th className="text-center py-4 px-4 font-semibold text-slate-700 w-[15%]">Status</th>
                          </tr>
                        </thead>
                        <tbody className="h-full">
                          {
                            formData?.results?.map((r, i) => {
                              const t = selectedTestOrder?.tests?.[i];
                              const testObj = (t?.test && typeof t.test === 'object') ? t.test : {};
                              const isTextType = isTextTypeReport(testObj?.testGroup?.superGroup);
                              const testDisplayName = r?.testName || testObj.testName || testObj.title || t?.testName || (typeof t?.test === "string" ? t.test : "N/A");
                              return (
                                <tr className="border-b border-gray-50 hover:bg-gray-100 transition-colors rounded-lg" key={i}>
                                  <td className="py-4 px-4 font-medium text-slate-600">
                                    {i + 1}
                                  </td>
                                  <td className="py-4 px-4 font-medium text-slate-600">
                                    {testDisplayName}
                                  </td>
                                  <td className="py-4 px-4" colSpan={isTextType ? 3 : 1}>
                                    {isTextType ?
                                      <Input.TextArea
                                        placeholder="Enter Value"
                                        autoSize={{ minRows: 3, maxRows: 3 }}
                                        value={formData?.results[i]?.value || ""}
                                        onChange={(e) => setFormTestValue(i, "value", e.target.value)}
                                        className="border-2 border-gray-200 rounded-lg hover:border-blue-300 focus:border-blue-500 transition-all duration-200"
                                      /> : <Input
                                        placeholder="Enter Value"
                                        value={formData?.results[i]?.value || ""}
                                        onChange={(e) => setFormTestValue(i, "value", e.target.value)}
                                        className="border-2 border-gray-200 rounded-lg hover:border-blue-300 focus:border-blue-500 transition-all duration-200"
                                      />}
                                  </td>
                                  {!isTextType && (
                                    <>
                                      <td className="py-4 px-4">
                                        <TestUnitSelect
                                          onChange={val => setFormTestValue(i, "testUnit", val)}
                                          value={formData?.results[i]?.testUnit || ""}
                                          className="min-w-36"
                                        />
                                      </td>
                                      <td className="py-4 px-4 text-center text-slate-600">
                                        <TestValueSelect
                                          value={formData?.results[i]?.normalValue || ""}
                                          onChange={nv => setFormTestValue(i, "normalValue", nv)}
                                          className="min-w-36"
                                        />
                                      </td>
                                    </>)
                                  }
                                  <td className="py-4 px-4 text-center text-slate-600">
                                    {(formData?.results[i]?.status) === "completed" ? (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                        Complete
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                                        Pending
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                      {!selectedTestOrder && <h1 className="text-center w-full p-5 text-red-600 font-semibold">No test order selected.</h1>}
                      {selectedTestOrder && (!formData?.results || formData?.results.length === 0) && (!selectedTestOrder?.tests || selectedTestOrder?.tests.length === 0) && <h1 className="text-center w-full p-5 text-red-600 font-semibold">No test found.</h1>}
                    </div>
                  </div>
                </div>

                {/* Patient Selection Section / Comment Section */}
                {!isEditMode ? (
                  <PatientSection
                    patients={patients}
                    testOrders={(patients || []).flatMap(p => p.testOrders || [])}
                    selectedPatient={selectedPatient}
                    selectedTestOrder={selectedTestOrder}
                    onSelect={(p) => handlePatientSelect(p)}
                    onSelectTestOrder={handleTestOrderSelect}
                    onPatientReload={fetchPatients}
                    comment={formData.comment}
                    onCommentChange={handleCommentChange}
                  />
                ) : (
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 p-6">
                    <h3 className="font-semibold text-lg text-slate-700 mb-3">Comments</h3>
                    <Input.TextArea
                      rows={4}
                      value={formData.comment || ""}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      placeholder="Enter any comments regarding the report here..."
                      className="rounded-xl border-2 border-gray-200 focus:border-blue-500 hover:border-gray-300 transition-all duration-200"
                    />
                  </div>
                )}

                {/* Action Section */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 px-8 py-3">
                  <div className="flex items-center gap-5">
                    {selectedTestOrder && (
                      <div className="flex items-center space-x-4 flex-1 gap-5">
                        <span className="text-lg font-semibold text-slate-700">Prepared by</span>
                        <div className="flex-1 min-w-[350px]">
                          <DoctorSelect
                            selectedId={formData.preparedBy || undefined}
                            onSelectID={(id) => setFormdata((prev) => ({ ...prev, preparedBy: id }))}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-3 ml-auto">
                      <Button
                        type="primary"
                        onClick={handleReportSave}
                        disabled={isSaving || !selectedTestOrder}
                        title={!selectedTestOrder ? "Select a test order first" : ""}
                        className={`bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 py-4 rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-lg h-auto ${isSaving || !selectedTestOrder ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                      >
                        {isSaving ? 'Saving...' : 'Save Report'}
                      </Button>

                      <Button
                        onClick={() => navigate(-1)}
                        className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white font-semibold px-6 py-4 rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-lg h-auto"
                      >
                        Exit
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <SelectTestOrderModal
          open={currentModal === "select-test-order"}
          selectableTestOrders={selectableTestOrders}
          onCancel={() => {
            setSelectedPatient(null);
            setCurrentModal("");
          }}
          onSelectOrder={(order) => {
            handleTestOrderSelect(order);
            toast.success("Test order selected.", { toastId: "test-order-selected" });
          }}
        />

        <PatientInfoModal
          open={currentModal === "patient-info"}
          selectedPatient={selectedPatient}
          selectedTestOrder={selectedTestOrder}
          onClose={() => setCurrentModal("")}
        />

        <GroupedTestModal
          open={currentModal === "grouped-test"}
          groupedTests={getGroupedTests()}
          onClose={() => setCurrentModal("")}
        />
      </div>
    </div>
  );
};

export default AddReport;
